import { FC, useState, useEffect } from 'react';
import { useOrganization } from 'src/context/OrganizationContext';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { RuleCreationStepper } from 'components/pricing/steps/RuleCreationStepper';
import { trpc } from 'src/trpc';
import {
  Configuration,
  ConfigurationTypeEnum,
  PricingRuleFormData,
  Scope,
  ContainerMaterialMatrixConfiguration,
  ContainerPeriodPricingConfiguration,
  PayAsYouGoPricingConfiguration,
  TonnagePricingConfiguration,
  AutoDiscountConfiguration,
  DiscountConfiguration,
  DiscountTypeEnum,
} from '@libs/schemas';

// Import extracted components
import { LocationSelectionItem } from 'components/pricing/components/LocationSelector';
import { BasicInfoStep } from 'components/pricing/components/BasicInfoStep';
import { ConfigurationStep } from 'components/pricing/components/ConfigurationStep';
import { ScopeAndTimingStep } from 'components/pricing/components/ScopeAndTimingStep';
import { ReviewStep } from 'components/pricing/components/ReviewStep';
import {
  MatrixData,
  ApiMaterial,
  MatrixCombination,
} from 'components/pricing/components/ContainerMaterialMatrix';
import { PeriodPricingTier } from 'components/pricing/components/ContainerPeriodMatrix';
import { PayAsYouGoContainerRate } from 'components/pricing/components/ContainerPayAsYouGoMatrix';

// Union type for configuration field values
export type ConfigurationFieldValue =
  | string
  | number
  | boolean
  | Record<string, Record<string, unknown>>
  | undefined;

interface ErrorRecord {
  [key: string]: string;
}

interface PricingRuleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: Partial<PricingRuleFormData>;
  onSubmit: (data: PricingRuleFormData) => void;
}

const steps = ['Basic Information', 'Configuration', 'Scope & Timing', 'Review'];

export const PricingRuleSheet: FC<PricingRuleSheetProps> = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
}) => {
  const { organizationId } = useOrganization();
  const currentOrganizationId = organizationId || '';

  // Fetch real data from tRPC APIs
  const { data: containerTypesResponse, isLoading: _loadingContainers } =
    trpc.admin.containerType.getAllContainerTypes.useQuery({
      offset: 0,
      limit: 1000, // Load all container types
      search: '',
      archived: 'active', // Only get active container types
    });

  const { data: materialResponse, isLoading: _loadingMaterials } =
    trpc.admin.material.getAllMaterials.useQuery({ archived: 'active' });

  const containers = containerTypesResponse?.containerTypes || [];
  const materialsData = (materialResponse?.materials || []) as ApiMaterial[];

  const hasEnabledCombinations = (matrix: Record<string, Record<string, unknown>>): boolean => {
    return Object.values(matrix).some((materialData) => {
      if (typeof materialData === 'object' && materialData !== null) {
        return Object.values(materialData).some((combination) => {
          if (typeof combination === 'object' && combination !== null && 'enabled' in combination) {
            return combination.enabled === true;
          }
          return false;
        });
      }
      return false;
    });
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PricingRuleFormData>({
    name: '',
    configuration: {
      type: ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX,
      data: {
        matrixData: {},
      },
    },
    scope: {},
    precedence: 100,
    effectiveFrom: '',
    effectiveTo: undefined,
    canCombine: true,
    organizationId: currentOrganizationId, // Use current organization from cookies
    active: true,
    ...initialData,
  });

  const [scopeType, setScopeType] = useState('global');
  const [selectedLocations, setSelectedLocations] = useState<LocationSelectionItem[]>([]);
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<ErrorRecord>({});

  // Reset form when sheet opens for new rule creation
  useEffect(() => {
    if (open && !initialData) {
      // Reset form to initial state for new rule creation
      setCurrentStep(1);
      setFormData({
        name: '',
        configuration: {
          type: ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX,
          data: {
            matrixData: {},
          },
        },
        scope: {},
        precedence: 100,
        effectiveFrom: '',
        effectiveTo: undefined,
        canCombine: true,
        organizationId: currentOrganizationId,
        active: true,
      });
      setScopeType('global');
      setSelectedLocations([]);
      setCustomerIds([]);
      setErrors({});
    }
  }, [open, initialData, currentOrganizationId]);

  // Update the useEffect to handle configurations properly
  useEffect(() => {
    if (initialData?.scope) {
      // Initialize locations from zip codes - convert to LocationSelectionItem format
      const scopeData = initialData.scope;
      if (scopeData.zipCodes && Array.isArray(scopeData.zipCodes)) {
        const locationItems: LocationSelectionItem[] = scopeData.zipCodes.map((zipCode) => ({
          type: 'zipcode' as const,
          value: zipCode,
          display: zipCode, // We'll improve this later to include city info
          zipCodes: [zipCode],
        }));
        setSelectedLocations(locationItems);
      }

      // Set scope type based on non-zip code criteria
      if (scopeData.customerClassification) {
        setScopeType('classification');
      } else if (scopeData.customerIds) {
        setScopeType('customers');
        setCustomerIds(Array.isArray(scopeData.customerIds) ? scopeData.customerIds : []);
      } else {
        setScopeType('global');
      }
    }

    // Initialize configuration from initialData and clean up the data structure
    if (initialData?.configuration) {
      const cleanedConfiguration = { ...initialData.configuration };

      // Clean up the data structure based on configuration type
      if (cleanedConfiguration.type === ConfigurationTypeEnum.TONNAGE_PRICING) {
        const currentData = cleanedConfiguration.data as Record<string, unknown>;
        cleanedConfiguration.data = {
          perTonRateInCents: (currentData.perTonRateInCents as number) || 0,
          minimumTonnage: (currentData.minimumTonnage as number) || 0,
        };
      } else if (cleanedConfiguration.type === ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX) {
        const currentData = cleanedConfiguration.data as Record<string, unknown>;
        cleanedConfiguration.data = {
          matrixData:
            (currentData.matrixData as Record<string, Record<string, MatrixCombination>>) || {},
        };
      } else if (cleanedConfiguration.type === ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING) {
        const currentData = cleanedConfiguration.data as Record<string, unknown>;
        cleanedConfiguration.data = {
          containerPeriods:
            (currentData.containerPeriods as Record<string, PeriodPricingTier>) || {},
        };
      } else if (cleanedConfiguration.type === ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING) {
        const currentData = cleanedConfiguration.data as Record<string, unknown>;
        cleanedConfiguration.data = {
          containerRates:
            (currentData.containerRates as Record<string, PayAsYouGoContainerRate>) || {},
        };
      } else if (
        cleanedConfiguration.type === ConfigurationTypeEnum.AUTO_DISCOUNT ||
        cleanedConfiguration.type === ConfigurationTypeEnum.DISCOUNT
      ) {
        const currentData = cleanedConfiguration.data as Record<string, unknown>;
        cleanedConfiguration.data = {
          discountPercent: (currentData.discountPercent as number) || 0,
          discountType:
            (currentData.discountType as DiscountTypeEnum) || DiscountTypeEnum.PERCENTAGE,
          discountAmountInCents: (currentData.discountAmountInCents as number) || undefined,
        };
      }

      setFormData((prev) => ({
        ...prev,
        configuration: cleanedConfiguration as Configuration,
      }));
    }
  }, [initialData]);

  // Update canCombine when rule type changes (only for new rules, not when editing)
  useEffect(() => {
    // Only auto-set canCombine for new rules (when initialData is not provided)
    if (!initialData) {
      const hasDiscount =
        formData.configuration.type === ConfigurationTypeEnum.AUTO_DISCOUNT ||
        formData.configuration.type === ConfigurationTypeEnum.DISCOUNT;
      setFormData((prev) => ({ ...prev, canCombine: !hasDiscount }));
    }
  }, [formData.configuration.type, initialData]);

  // Validation function that doesn't cause side effects
  const getValidationErrors = (step: number): ErrorRecord => {
    const newErrors: ErrorRecord = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = 'Rule name is required';
        break;
      case 2:
        if (!formData.configuration.type) {
          newErrors.configuration = 'Configuration type is required';
        } else {
          const config = formData.configuration;
          switch (config.type) {
            case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX: {
              const matrixConfig = config as ContainerMaterialMatrixConfiguration;
              const hasEnabled = hasEnabledCombinations(
                matrixConfig.data.matrixData as Record<string, Record<string, unknown>>,
              );
              if (!hasEnabled) {
                newErrors.matrix = 'At least one container + material combination must be enabled';
              } else {
                // Validate that enabled fixed pricing combinations have all required fields
                const matrixData = matrixConfig.data.matrixData;
                let hasIncompleteEnabledCombinations = false;

                Object.values(matrixData).forEach((materialData) => {
                  Object.values(materialData).forEach((combination) => {
                    if (combination.enabled) {
                      const hasFixedPrice =
                        combination.fixedPriceInCents && combination.fixedPriceInCents > 0;
                      const hasIncludedDays =
                        combination.includedDays && combination.includedDays > 0;
                      const hasDailyOverageFee =
                        combination.dailyOverageFeeInCents !== undefined &&
                        combination.dailyOverageFeeInCents >= 0;

                      if (!hasFixedPrice || !hasIncludedDays || !hasDailyOverageFee) {
                        hasIncompleteEnabledCombinations = true;
                      }
                    }
                  });
                });

                if (hasIncompleteEnabledCombinations) {
                  newErrors.matrix =
                    'All enabled combinations must have Fixed Price, Included Days, and Daily Overage Fee set';
                }
              }
              break;
            }
            case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
              const periodConfig = config as ContainerPeriodPricingConfiguration;
              const hasContainers =
                periodConfig.data.containerPeriods &&
                Object.keys(periodConfig.data.containerPeriods).length > 0;
              if (!hasContainers) {
                newErrors.periodMatrix = 'At least one container must be configured';
              }
              break;
            }
            case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING: {
              const payAsYouGoConfig = config as PayAsYouGoPricingConfiguration;
              const hasContainers =
                payAsYouGoConfig.data.containerRates &&
                Object.keys(payAsYouGoConfig.data.containerRates).length > 0;
              if (!hasContainers) {
                newErrors.containerRates = 'At least one container must be configured';
              }
              break;
            }
            case ConfigurationTypeEnum.TONNAGE_PRICING: {
              const tonnageConfig = config as TonnagePricingConfiguration;
              if (
                !tonnageConfig.data.perTonRateInCents ||
                tonnageConfig.data.perTonRateInCents <= 0
              ) {
                newErrors.perTonRate = 'Per-ton rate must be greater than 0';
              }
              if (!tonnageConfig.data.minimumTonnage || tonnageConfig.data.minimumTonnage <= 0) {
                newErrors.minimumTonnage = 'Minimum tonnage must be greater than 0';
              }
              break;
            }
            case ConfigurationTypeEnum.AUTO_DISCOUNT: {
              const autoDiscountConfig = config as AutoDiscountConfiguration;
              if (autoDiscountConfig.data.discountType === DiscountTypeEnum.PERCENTAGE) {
                if (
                  !autoDiscountConfig.data.discountPercent ||
                  autoDiscountConfig.data.discountPercent <= 0 ||
                  autoDiscountConfig.data.discountPercent > 100
                ) {
                  newErrors.discountPercent = 'Discount must be between 1 and 100%';
                }
              } else {
                if (
                  !autoDiscountConfig.data.discountAmountInCents ||
                  autoDiscountConfig.data.discountAmountInCents <= 0
                ) {
                  newErrors.discountAmountInCents = 'Discount amount must be greater than 0';
                }
              }
              break;
            }
            case ConfigurationTypeEnum.DISCOUNT: {
              const discountConfig = config as DiscountConfiguration;
              if (discountConfig.data.discountType === DiscountTypeEnum.PERCENTAGE) {
                if (
                  !discountConfig.data.discountPercent ||
                  discountConfig.data.discountPercent <= 0 ||
                  discountConfig.data.discountPercent > 100
                ) {
                  newErrors.discountPercent = 'Discount must be between 1 and 100%';
                }
              } else {
                if (
                  !discountConfig.data.discountAmountInCents ||
                  discountConfig.data.discountAmountInCents <= 0
                ) {
                  newErrors.discountAmountInCents = 'Discount amount must be greater than 0';
                }
              }
              break;
            }
          }
        }
        break;
      case 3:
        if (!formData.effectiveFrom) newErrors.effectiveFrom = 'Effective from date is required';
        // effectiveTo is now optional - no validation needed
        break;
    }

    return newErrors;
  };

  // Validation function that updates errors and returns boolean
  const validateStep = (step: number) => {
    const newErrors = getValidationErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if current step is valid (for button disabled state)
  const isCurrentStepValid = Object.keys(getValidationErrors(currentStep)).length === 0;

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    setErrors({}); // Clear errors when going back
  };

  const handleSubmit = () => {
    if (!validateStep(currentStep)) return;

    const scope: Scope = {};

    // Add scope type criteria
    if (scopeType === 'classification' && formData.scope?.customerClassification) {
      scope.customerClassification = formData.scope.customerClassification;
    } else if (scopeType === 'customers' && customerIds.length > 0) {
      scope.customerIds = customerIds;
    }
    // For global scope, we don't add any scope criteria

    // Convert selected locations to zip codes (flatten all zip codes from cities and individual selections)
    if (selectedLocations.length > 0) {
      const allZipCodes = selectedLocations.reduce<string[]>((acc, location) => {
        acc.push(...location.zipCodes);
        return acc;
      }, []);

      // Remove duplicates
      scope.zipCodes = [...new Set(allZipCodes)];
    }

    const finalData = {
      ...formData,
      scope,
      organizationId: currentOrganizationId, // Ensure organizationId is set from current context
    };

    onSubmit(finalData);
    onOpenChange(false);
  };

  const updateConfigurationField = (field: string, value: ConfigurationFieldValue) => {
    const newConfiguration = { ...formData.configuration } as Record<string, unknown>;

    // Handle configuration type changes - reset data structure when type changes
    if (field === 'type') {
      const configurationType = value as ConfigurationTypeEnum;

      // Reset data structure based on the new configuration type
      switch (configurationType) {
        case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX:
          newConfiguration.data = {
            matrixData: {},
          };
          break;
        case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING:
          newConfiguration.data = {
            containerPeriods: {},
          };
          break;
        case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING:
          newConfiguration.data = {
            containerRates: {},
          };
          break;
        case ConfigurationTypeEnum.TONNAGE_PRICING:
          newConfiguration.data = {
            perTonRateInCents: 0,
            minimumTonnage: 0,
          };
          break;
        case ConfigurationTypeEnum.AUTO_DISCOUNT:
        case ConfigurationTypeEnum.DISCOUNT:
          newConfiguration.data = {
            discountType: DiscountTypeEnum.PERCENTAGE,
            discountPercent: 0,
          };
          break;
        default:
          newConfiguration.data = {};
      }

      newConfiguration[field] = value;
    } else if (field.includes('.')) {
      // Handle nested field paths like 'data.perTonRateInCents'
      const [parentField, childField] = field.split('.');
      const existingParentData = (newConfiguration[parentField] as Record<string, unknown>) || {};

      newConfiguration[parentField] = {
        ...existingParentData,
        [childField]: value,
      };
    } else {
      newConfiguration[field] = value;
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        configuration: newConfiguration as unknown as Configuration,
      };
      return updated;
    });
  };

  const updateMatrixData = (newMatrixData: MatrixData) => {
    const newConfiguration = {
      ...formData.configuration,
      data: {
        ...formData.configuration.data,
        matrixData: newMatrixData,
      },
    };

    setFormData((prev) => {
      const updated = { ...prev, configuration: newConfiguration };
      return updated;
    });
  };

  const updateContainerPeriods = (newContainerPeriods: Record<string, PeriodPricingTier>) => {
    const newConfiguration = {
      ...formData.configuration,
      data: {
        ...formData.configuration.data,
        containerPeriods: newContainerPeriods,
      },
    };

    setFormData((prev) => ({ ...prev, configuration: newConfiguration }));
  };

  const updateContainerRates = (newContainerRates: Record<string, PayAsYouGoContainerRate>) => {
    const newConfiguration = {
      ...formData.configuration,
      data: {
        ...formData.configuration.data,
        containerRates: newContainerRates,
      },
    };

    setFormData((prev) => ({ ...prev, configuration: newConfiguration }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            errors={errors}
            onFormDataChange={(updates) => {
              setFormData((prev) => {
                const updated = { ...prev, ...updates };
                return updated;
              });
            }}
          />
        );

      case 2:
        return (
          <ConfigurationStep
            configuration={formData.configuration as Configuration}
            errors={errors}
            containers={containers}
            materialsData={materialsData}
            onConfigurationChange={updateConfigurationField}
            onMatrixDataChange={updateMatrixData}
            onContainerPeriodsChange={updateContainerPeriods}
            onContainerRatesChange={updateContainerRates}
          />
        );

      case 3:
        return (
          <ScopeAndTimingStep
            formData={formData}
            errors={errors}
            scopeType={scopeType}
            selectedLocations={selectedLocations}
            customerIds={customerIds}
            onFormDataChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
            onScopeTypeChange={setScopeType}
            onSelectedLocationsChange={setSelectedLocations}
            onCustomerIdsChange={setCustomerIds}
          />
        );

      case 4:
        return (
          <ReviewStep
            formData={formData}
            selectedLocations={selectedLocations}
            customerIds={customerIds}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[800px] h-full p-0">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle>{initialData ? 'Edit Pricing Rule' : 'Create Pricing Rule'}</SheetTitle>
            <RuleCreationStepper steps={steps} currentStep={currentStep - 1} />
          </SheetHeader>

          <div className="overflow-y-auto px-6 py-6">{renderStepContent()}</div>

          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex justify-between w-full">
              <Button variant="outline" disabled={currentStep === 1} onClick={handlePrevious}>
                Previous
              </Button>

              <div className="flex gap-2">
                <SheetClose asChild>
                  <Button variant="outline">Cancel</Button>
                </SheetClose>

                {currentStep === steps.length ? (
                  <Button onClick={handleSubmit} disabled={Object.keys(errors).length > 0}>
                    {initialData ? 'Update Rule' : 'Create Rule'}
                  </Button>
                ) : (
                  <Button onClick={handleNext} disabled={!isCurrentStepValid}>
                    Next
                  </Button>
                )}
              </div>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
